import React, { useMemo, useState } from "react";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { FREEZER_FROST_SHARE_CREATE } from "../../../apiClient/endpoints";

const EXPIRY_OPTIONS = [
  { value: 24, label: "1 day (default)" },
  { value: 72, label: "3 days" },
  { value: 168, label: "7 days" },
  { value: 720, label: "30 days" },
];

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    try {
      const input = document.createElement("textarea");
      input.value = text;
      input.setAttribute("readonly", "");
      input.style.position = "fixed";
      input.style.left = "-9999px";
      document.body.appendChild(input);
      input.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(input);
      return ok;
    } catch (fallbackErr) {
      return false;
    }
  }
};

const FreezerFrostShareModal = ({ open, onClose, groupId, groupName, apiUrl, token }) => {
  const [expiresInHours, setExpiresInHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [copied, setCopied] = useState(false);

  const previewExpiry = useMemo(() => {
    const hours = Number(expiresInHours) > 0 ? Number(expiresInHours) : 24;
    return new Date(Date.now() + hours * 60 * 60 * 1000);
  }, [expiresInHours]);

  if (!open) return null;

  const createLink = async () => {
    if (!apiUrl) {
      setError("API URL is missing. Refresh the page and try again.");
      return;
    }
    setBusy(true);
    setError("");
    setCopied(false);
    try {
      const { data } = await axios.post(
        `${apiUrl}${FREEZER_FROST_SHARE_CREATE}`,
        {
          groupId,
          groupName: groupName || "",
          expiresInHours: Number(expiresInHours) > 0 ? Number(expiresInHours) : 24,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!data?.status) {
        setError(data?.message || "Could not create share link");
        return;
      }
      const path = data.data?.path || `/ff/live/${data.data?.token}`;
      const url = `${window.location.origin}${path}`;
      setShareUrl(url);
      setExpiresAt(data.data?.expiresAt || previewExpiry.toISOString());

      const didCopy = await copyText(url);
      setCopied(didCopy);
      if (didCopy) {
        setTimeout(() => setCopied(false), 4000);
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err.message || "Could not create share link";
      setError(status ? `${msg} (${status})` : msg);
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    if (!shareUrl) return;
    const didCopy = await copyText(shareUrl);
    setCopied(didCopy);
    if (didCopy) setTimeout(() => setCopied(false), 4000);
  };

  const handleClose = () => {
    setShareUrl("");
    setExpiresAt(null);
    setError("");
    setCopied(false);
    setExpiresInHours(24);
    onClose();
  };

  return (
    <div className="ff-share-modal__overlay" onClick={handleClose}>
      <div className="ff-share-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="ff-share-modal__head">
          <div>
            <h3>
              <ShareOutlinedIcon /> Share live dashboard
            </h3>
            <p>Anyone with the link can view this Freezer-Frost board — view only.</p>
          </div>
          <button type="button" className="ff-share-modal__close" onClick={handleClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="ff-share-modal__body">
          {!shareUrl ? (
            <>
              <label className="ff-share-modal__field">
                <span>Link expiry</span>
                <select value={expiresInHours} onChange={(e) => setExpiresInHours(Number(e.target.value))}>
                  {EXPIRY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <p className="ff-share-modal__hint">
                If you don’t change this, the link expires in <strong>1 day</strong>. Preview expiry:{" "}
                {previewExpiry.toLocaleString()}
              </p>
              {error ? <p className="ff-share-modal__error">{error}</p> : null}
              <button type="button" className="ff-share-modal__primary" onClick={createLink} disabled={busy}>
                {busy ? "Creating…" : "Create share link"}
              </button>
            </>
          ) : (
            <>
              {copied ? (
                <div className="ff-share-modal__copied-banner" role="status">
                  <CheckIcon fontSize="small" />
                  Link copied to clipboard
                </div>
              ) : (
                <div className="ff-share-modal__notice">
                  Anyone with this link can view the live Freezer-Frost dashboard. They cannot edit or access the full app.
                </div>
              )}
              <label className="ff-share-modal__field">
                <span>Share URL</span>
                <div className="ff-share-modal__url-row">
                  <input type="text" readOnly value={shareUrl} onFocus={(e) => e.target.select()} />
                  <button type="button" className="ff-share-modal__copy" onClick={copyUrl}>
                    {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </label>
              <p className="ff-share-modal__hint">
                Expires: <strong>{expiresAt ? new Date(expiresAt).toLocaleString() : "—"}</strong>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FreezerFrostShareModal;
